import { useLayoutEffect, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Resets scroll to top on every route change BEFORE the browser paints,
 * eliminating the "see-bottom-of-new-page-then-jump-to-top" flicker.
 *
 * If the URL contains a hash, we still snap to the top first; the target
 * page's hash-effect (e.g. Landing's #pricing handler) takes over from
 * a clean starting position so cross-page anchor nav feels predictable.
 */
export default function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const prevPathRef = useRef(pathname);

  // Disable the browser's automatic scroll-restoration so React Router
  // is the sole authority on scroll position.
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  useLayoutEffect(() => {
    // Skip when only the hash changed within the same route — the hash-effect
    // on the page handles smooth-scrolling between in-page sections.
    if (prevPathRef.current === pathname) {
      prevPathRef.current = pathname;
      return;
    }
    prevPathRef.current = pathname;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, hash]);

  return null;
}
